import Session from '@getpopsure/session';
import { setAccountDataInQuestionnaire, submitAccount } from 'actions/account';
import { storeGenericQuestionnaireAnswer } from 'actions/genericQuestionnaire';
import { fetchAccountInfo } from 'actions/user';
import { AccountView } from 'components/AccountEmail/components/Account/Account.view';
import * as EmailComponents from 'components/AccountEmail/components/Email/Email';
import LoginModal from 'components/signupAuthentication/email/components/loginModal';
import { APIResponseError } from 'models/error';
import { UserWithBrokerMandate } from 'models/user';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { useDispatch, useSelector } from 'react-redux';
import { GenericQuestionnaireState } from 'reducers/genericQuestionnaire';
import { getAccountInfo, getEmail } from 'selectors/user';

import styles from './style.module.scss';

const { Title, Subtitle, Input, GoogleError, Actions } = EmailComponents;

interface Props {
  redirectPath: string;
  loading?: boolean;
  error?: APIResponseError;
  handleValidateAccount: (
    email: string,
    onUserExists: (userExists: boolean) => void
  ) => void;
  verticalId: keyof GenericQuestionnaireState;
  redirectExistingUserPath?: string;
  storedAnswer?: string;
  onContinueWithCurrentAccount?: (
    account: UserWithBrokerMandate | null
  ) => void;
}

const Email = ({
  redirectPath,
  redirectExistingUserPath,
  loading,
  error,
  handleValidateAccount,
  verticalId,
  storedAnswer = '',
  onContinueWithCurrentAccount,
}: Props) => {
  const { isAuthenticated } = Session;
  const dispatch = useDispatch();
  const account = useSelector(getAccountInfo);
  const accountEmail = useSelector(getEmail);
  const [email, setEmail] = useState(storedAnswer);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [requiresLoginCode, setRequiresLoginCode] = useState(false);
  const [googleLoginError, setGoogleLoginError] = useState(false);
  const [continueWithEmail, setContinueWithEmail] = useState(false);
  const [createNewAccount, setCreateNewAccount] = useState(false);
  const valid = email.length > 0 && termsAccepted;

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchAccountInfo());
      const defaultEmail = account?.email ?? '';
      setEmail(defaultEmail);
    }
  }, [dispatch, isAuthenticated, account?.email]);

  const handleModalClose = () => {
    setRequiresLoginCode(false);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleSetTermsAccepted = () => {
    setTermsAccepted((value) => !value);
  };

  const handleOnLogin = () => {
    if (account?.email === email) {
      dispatch(storeGenericQuestionnaireAnswer(verticalId, { account }));
    }
  };

  const handleOnGoogleLogin = () => {
    dispatch(
      setAccountDataInQuestionnaire(
        redirectExistingUserPath || redirectPath,
        verticalId
      )
    );

    handleOnLogin();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (valid) {
      handleValidateAccount(email, (userExists) => {
        if (!userExists) {
          dispatch(
            storeGenericQuestionnaireAnswer(verticalId, { account: null })
          );
        }

        setRequiresLoginCode(userExists);
      });
    }
  };

  const onContinueWithAccount = () => {
    onContinueWithCurrentAccount?.(account || null);
    dispatch(submitAccount(verticalId, redirectPath, account));
  };

  const isSameEmail = accountEmail === email;
  if (
    account &&
    account.firstName &&
    account.lastName &&
    isAuthenticated &&
    !createNewAccount &&
    isSameEmail
  ) {
    return (
      <AccountView
        email={account.email}
        name={{
          firstName: account.firstName,
          lastName: account.lastName,
        }}
        handleContinueWithCurrentAccount={onContinueWithAccount}
        handleCreateNewAccount={() => setCreateNewAccount(true)}
      />
    );
  }

  const isEmailSet = continueWithEmail || email.length > 0;
  return (
    <>
      <LoginModal
        email={email}
        isOpen={requiresLoginCode}
        onClose={handleModalClose}
        onLogin={handleOnLogin}
        redirectPath={redirectExistingUserPath || redirectPath}
        verticalId={verticalId}
      />
      <form className={styles.container} onSubmit={handleSubmit}>
        <div className="p-body">
          <Title />
          <Subtitle />
          <Input
            show={isEmailSet}
            showTerms
            value={email}
            emailInputOnChange={handleInputChange}
            tAndCInputOnChange={handleSetTermsAccepted}
          />
          <AnimateHeight duration={300} height={error ? 'auto' : 0}>
            <div className="p-p--small p-notice--warning">{error?.message}</div>
          </AnimateHeight>
          {googleLoginError && <GoogleError />}
          <Actions
            isEmailSet={isEmailSet}
            disabled={!valid}
            loading={loading}
            setContinueWithEmail={setContinueWithEmail}
            googleOnSuccess={handleOnGoogleLogin}
            googleOnError={() => setGoogleLoginError(true)}
          />
        </div>
      </form>
    </>
  );
};

export default Email;
